import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, MapPin, Users, Package, Settings, Loader2, 
  ChevronRight, Clock, AlertCircle, CreditCard, ExternalLink, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyBookingsPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchBookings();
  }, [token, navigate]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings/my-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const isUpcoming = (dateStr) => {
    if (!dateStr) return true;
    try {
      const eventDate = new Date(dateStr);
      return eventDate > new Date();
    } catch {
      return true;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
      case 'confirmed':
        return <Badge className="bg-green-500">Confirmed</Badge>;
      case 'deposit_paid':
        return <Badge className="bg-indigo-500">Deposit Paid</Badge>;
      case 'modified':
        return <Badge className="bg-blue-500">Modified</Badge>;
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const upcomingBookings = bookings.filter(b => isUpcoming(b.event_date));
  const pastBookings = bookings.filter(b => !isUpcoming(b.event_date));

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold" data-testid="my-bookings-title">My Bookings</h1>
          <p className="text-muted-foreground mt-1">
            Manage and modify your event bookings
          </p>
        </div>

        {bookings.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No bookings yet</h3>
              <p className="text-muted-foreground mb-4">
                Start planning your next event!
              </p>
              <Button onClick={() => navigate('/plan')}>
                Plan an Event
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">
                Upcoming ({upcomingBookings.length})
              </TabsTrigger>
              <TabsTrigger value="past" data-testid="tab-past">
                Past ({pastBookings.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming">
              {upcomingBookings.length === 0 ? (
                <Card className="text-center py-8">
                  <CardContent>
                    <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No upcoming bookings</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {upcomingBookings.map(booking => (
                    <BookingCard 
                      key={booking.booking_id} 
                      booking={booking} 
                      formatDate={formatDate}
                      getStatusBadge={getStatusBadge}
                      onManage={() => navigate(`/my-events/${booking.booking_id}/manage`)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="past">
              {pastBookings.length === 0 ? (
                <Card className="text-center py-8">
                  <CardContent>
                    <Clock className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No past bookings</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {pastBookings.map(booking => (
                    <BookingCard 
                      key={booking.booking_id} 
                      booking={booking} 
                      formatDate={formatDate}
                      getStatusBadge={getStatusBadge}
                      isPast
                      onManage={() => navigate(`/my-events/${booking.booking_id}/manage`)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function BookingCard({ booking, formatDate, getStatusBadge, isPast, onManage }) {
  const navigate = useNavigate();
  const itemCount = booking.items?.length || 0;
  const plan = booking.payment_plan;
  const hasPlan = plan?.has_plan;
  
  return (
    <Card 
      className={`overflow-hidden hover:shadow-md transition-shadow ${isPast ? 'opacity-75' : ''}`}
      data-testid={`booking-card-${booking.booking_id}`}
    >
      <CardContent className="p-0">
        <div className="flex items-stretch">
          {/* Date Indicator */}
          <div className="w-24 bg-indigo-500 text-foreground flex flex-col items-center justify-center p-4">
            <span className="text-2xl font-bold">
              {booking.event_date ? new Date(booking.event_date).getDate() : '?'}
            </span>
            <span className="text-sm">
              {booking.event_date ? new Date(booking.event_date).toLocaleDateString('en-US', { month: 'short' }) : 'TBD'}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-lg">{booking.title || 'Event'}</h3>
                  {getStatusBadge(booking.status)}
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(booking.event_date)}
                  </span>
                  {booking.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {booking.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {booking.guest_count} guests
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold">${booking.total?.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">{itemCount} service(s)</p>
              </div>
            </div>

            {/* Payment Plan Status */}
            {hasPlan && (
              <div className="mt-3 flex items-center gap-3 p-2.5 rounded-lg bg-muted/50 border" data-testid={`plan-status-${booking.booking_id}`}>
                <CreditCard className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {plan.deposit_status === 'paid' ? (
                      <Badge className="bg-green-500 text-white border-0 text-xs"><Check className="h-3 w-3 mr-1" />Deposit Paid</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">Deposit Pending</Badge>
                    )}
                    {plan.total_installments > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {plan.paid_installments}/{plan.total_installments} payments made
                      </span>
                    )}
                    {plan.plan_status === 'completed' && (
                      <Badge className="bg-green-500 text-white border-0 text-xs"><Check className="h-3 w-3 mr-1" />Paid in Full</Badge>
                    )}
                  </div>
                  {plan.remaining > 0.01 && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      ${plan.total_paid.toFixed(2)} paid · ${plan.remaining.toFixed(2)} remaining
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0 text-xs"
                  onClick={(e) => { e.stopPropagation(); navigate(`/invoice/${booking.booking_id}`); }}
                  data-testid={`view-invoice-${booking.booking_id}`}
                >
                  <ExternalLink className="h-3 w-3 mr-1" /> Invoice
                </Button>
              </div>
            )}

            {/* Modification indicator */}
            {booking.modifications?.length > 0 && (
              <div className="mt-2 flex items-center gap-1 text-sm text-blue-600">
                <AlertCircle className="h-4 w-4" />
                <span>{booking.modifications.length} modification(s)</span>
              </div>
            )}

            {/* Actions */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {booking.store_credit_applied > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Credit applied: ${booking.store_credit_applied.toFixed(2)}
                  </Badge>
                )}
              </div>
              {!isPast && (
                <Button variant="outline" size="sm" onClick={onManage} data-testid={`manage-btn-${booking.booking_id}`}>
                  <Settings className="mr-2 h-4 w-4" />
                  Manage Booking
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              )}
              {isPast && (
                <Button variant="ghost" size="sm" onClick={onManage}>
                  View Details
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
